import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ComparatorElectric } from 'src/app/components/account/models/ComparatorElectric';
import { ComparatorGas } from 'src/app/components/account/models/ComparatorGas';
import { CityDetail, CountyDetailsElectricity, CountyDetailsGas } from 'src/app/components/account/models/CountyDetails';
import { ElectricComparatorResult } from 'src/app/components/account/models/ElectricComparatorResult';
import { GasComparatorResult } from 'src/app/components/account/models/GasComparatorResult';
import { HandleError, HttpErrorHandler } from 'src/app/shared/service/error.service';
import { UtilService } from 'src/app/shared/service/util.service';
import { environment } from '../../../../environments/environment';
import * as moment from "moment/moment";

@Injectable({
  providedIn: 'root'
})
export class PublicComparatorService {

  public gasComparatorResults: GasComparatorResult[];
  public electricComparatorResults: ElectricComparatorResult[];
  private getJudeteUrl = environment.comparatorBaseUrl + '/comparator/api/index.php?request=get-judete';
  private getLocalitatiUrl = environment.comparatorBaseUrl + '/comparator/api/index.php?request=get-localitati&judet=';
  private getComparatorGasOferte = environment.comparatorBaseUrl + '/comparator/api/index.php?request=comparator-gaz';
  private getComparatorElectricOferte = environment.comparatorBaseUrl + '/comparator/api/index.php?request=comparator-electric';
  private getOfertaByCodeElectricityUrl = environment.comparatorBaseUrl + '/comparator/api/index.php?request=get-oferta-by-code';
  private getOfertaByCodeGasUrl = environment.comparatorBaseUrl + '/comparator/api/index.php?request=get-oferta-gaz-by-code';
  private readonly errorHandler: HandleError;

  constructor(private http: HttpClient,
    private httpErrorHandler: HttpErrorHandler,
    private utils: UtilService
  ) {
    this.errorHandler = httpErrorHandler.createHandleError('PublicComparatorService');
  }

  getCountiesElectricity(): Observable<CountyDetailsElectricity[]> {
    return this.http.get<CountyDetailsElectricity[]>(this.getJudeteUrl)
      .pipe(
        catchError(this.errorHandler('getCountiesElectricity', null))
      );
  }

  getCountiesGas(): Observable<CountyDetailsGas[]> {
    return this.http.get<CountyDetailsGas[]>(this.getJudeteUrl + '-gaz')
      .pipe(
        catchError(this.errorHandler('getCountiesGas', null))
      );
  }

  getCities(county: string): Observable<CityDetail[]> {
    return this.http.get<CityDetail[]>(this.getLocalitatiUrl + county)
      .pipe(
        catchError(this.errorHandler('getCities', null))
      );
  }

  getGasOfferByCode(offerCode: string, countyName: string): Observable<GasComparatorResult[]> {
    return this.http.get<GasComparatorResult>(this.getOfertaByCodeGasUrl + `&cod_oferta=${offerCode}` +
      `&judet=${countyName}`)
      .pipe(
        catchError(this.errorHandler('getGasOfferByCode', null))
      );
  }

  getElectricityOfferByCode(offerCode: string, countyName: string): Observable<ElectricComparatorResult[]> {
    return this.http.get<ElectricComparatorResult>(this.getOfertaByCodeElectricityUrl + `&cod_oferta=${offerCode}` +
      `&judet=${countyName}`)
      .pipe(
        catchError(this.errorHandler('getElectricityOfferByCode', null))
      );
  }

  getGasOffers(comparatorGasDTO: ComparatorGas): Observable<GasComparatorResult[]> {
    let params = new HttpParams()
      .append('tip_client', comparatorGasDTO.finalClientType)
      .append('modalitate_racordare', comparatorGasDTO.connectionMethod)
      .append('transee_de_consum', comparatorGasDTO.annualConsumptionCategory)
      .append('tip_pret_unitar', comparatorGasDTO.unitSupplyPriceType)
      .append('id_judet', comparatorGasDTO.county)
      .append('id_siruta', comparatorGasDTO.city)
      .append('consum_mwh', comparatorGasDTO.estimatedMonthlyConsumptionMwh)
      .append('pret_furnizare_mwh', comparatorGasDTO.supplyPriceLeiMwh)
      .append('componenta_fixa', comparatorGasDTO.invoiceSubscriptionLeiZi)
      .append('suplimentar_componenta_fixa', comparatorGasDTO.subscription)
      .append('termen_plata', comparatorGasDTO.paymentTerm)
      .append('tip_oferta', comparatorGasDTO.offerType)
      .append('tip_pret', comparatorGasDTO.gasPriceType)
      .append('durata_contractului', comparatorGasDTO.contractDuration)
      .append('garantii', comparatorGasDTO.guarantees)
      .append('frecventa_emitere_factura', comparatorGasDTO.invoiceFrequency)
      .append('data_start_aplicare', comparatorGasDTO.offerDate);
    if (this.utils.nonNullAndNonUndefined(comparatorGasDTO.convention)) {
      comparatorGasDTO.convention.forEach((convention: string) => {
        params = params.append('modul_transmitere_factura[]', convention);
      })
    }
    let gasComparatorResultObservable = this.http.get<GasComparatorResult[]>(this.getComparatorGasOferte, { params })
      .pipe(
        catchError(this.errorHandler('getGasOffers', null)));
    gasComparatorResultObservable
      .subscribe((gasComparatorResults: GasComparatorResult[]) => {
        this.gasComparatorResults = gasComparatorResults;
      });
    return gasComparatorResultObservable;
  }

  getElectricOffers(comparatorElectricDTO: ComparatorElectric): Observable<ElectricComparatorResult[]> {
    let params = new HttpParams()
      .append('tip_oferta', comparatorElectricDTO.offerType)
      .append('data_start_aplicare', comparatorElectricDTO.offerDate)
      .append('tip_client', comparatorElectricDTO.clientType)
      .append('tip_pret', comparatorElectricDTO.priceType)
      .append('consum_anual', comparatorElectricDTO.currentBillConsumption * 12)
      .append('consum_lunar', comparatorElectricDTO.currentBillConsumption)
      .append('valoare_factura_curenta', comparatorElectricDTO.currentInvoiceValue)
      .append('nivel_tensiune', comparatorElectricDTO.voltageLevel)
      .append('id_zona', comparatorElectricDTO.zoneId)
      .append('tip_produs', comparatorElectricDTO.productType)
      .append('perioada_contract', comparatorElectricDTO.contractDuration)
      .append('energie_regenerabila', comparatorElectricDTO.renewableEnergy)
      .append('factura_electronica', comparatorElectricDTO.convention)
      .append('frecventa_emitere_factura', comparatorElectricDTO.invoiceFrequency)
      .append('procent_zona_noapte', comparatorElectricDTO.nightConsumptionPercentage)
      .append('procent_zona_zi', comparatorElectricDTO.dayConsumptionPercentage)
      .append('frecventa_citire_contor', comparatorElectricDTO.counterReadingFrequency)
      .append('valoare_fixa', comparatorElectricDTO.fixedComponentOffers);
    if (this.utils.nonNullAndNonUndefined(comparatorElectricDTO.denumireFurnizor)) {
      params = params.append('denumire_furnizor', comparatorElectricDTO.denumireFurnizor);
    }

    let electricComparatorResultObservable = this.http.get<ElectricComparatorResult[]>(this.getComparatorElectricOferte, { params }).pipe(
      catchError(this.errorHandler('getElectricOffers', null)));
    electricComparatorResultObservable
      .subscribe((electricComparatorResults: ElectricComparatorResult[]) => {
        this.electricComparatorResults = electricComparatorResults;
      });
    return electricComparatorResultObservable;
  }

  public getComparatorDocumentTypes(comparatorType: string, supplierLicence: string,
                                    comparatorResult: string,
                                    clientPF: boolean, clientPJ: boolean): string[] {
    const documentsType: string[] = [];
    if (comparatorType === 'electric') {
      if (supplierLicence) {
        const electricComparatorResult = JSON.parse(comparatorResult);
        if (supplierLicence === electricComparatorResult.licenta_furnizor) {
          if (clientPF) {
            documentsType.push('client_furnizor_existent_casnic');
          } else if (clientPJ) {
            documentsType.push('client_furnizor_existent_noncasnic');
          }
        } else {
          if (clientPF) {
            documentsType.push('client_furnizor_nou_casnic');
          } else if (clientPJ) {
            documentsType.push('client_furnizor_nou_noncasnic');
          }
        }
      } else {
        if (clientPF) {
          documentsType.push('client_nou_casnic');
        } else if (clientPJ) {
          documentsType.push('client_nou_noncasnic');
        }
      }
    } else if (comparatorType === 'gas') {
      if (supplierLicence) {
        const gasComparatorResult = JSON.parse(comparatorResult);
        if (supplierLicence === gasComparatorResult.licenta_furnizor) {
          if (clientPF) {
            documentsType.push('client_furnizor_existent_casnic_pf');
          } else if (clientPJ) {
            documentsType.push('client_furnizor_existent_noncasnic', 'client_furnizor_existent_casnic_pj');
          }
        } else {
          if (clientPF) {
            documentsType.push('client_furnizor_nou_casnic_pf');
          } else if (clientPJ) {
            documentsType.push('client_furnizor_nou_noncasnic', 'client_furnizor_nou_casnic_pj');
          }
        }
      } else {
        if (clientPF) {
          documentsType.push('client_nou_casnic_pf');
        } else if (clientPJ) {
          documentsType.push('client_nou_noncasnic', 'client_nou_casnic_pj');
        }
      }
    }

    return documentsType;
  }
}
